import React, { useEffect, useRef, useState } from 'react';

const PivotTimeline = () => {
  const [visibleItems, setVisibleItems] = useState(new Set());
  const [expandedItem, setExpandedItem] = useState(null);
  const timelineRef = useRef(null);

  const timelineEvents = [
    {
      date: 'In Active Development',
      title: 'WeatherWise.ai',
      subtitle: 'Private Project · VectorWorkX',
      description: 'A conversational interface over real weather model outputs. Surfaces observations and NWP model data from Xweather MCP, interprets them in plain language, and grounds explanations in published atmospheric science research via RAG.',
      collaborator: 'Co-building with Yavar Khan — ML/AI Engineer (MS Computer Science, SUNY Buffalo)',
      type: 'project',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
        </svg>
      ),
      highlights: ['LangGraph', 'MCP', 'RAG', 'Claude', 'Pinecone'],
      features: [
        'Xweather MCP integration surfacing live observations and model-generated forecasts',
        'RAG pipeline over NOAA, NASA, and AMS publications for science-backed explanations',
        'LLM-driven intent router with structured-output classification and dynamic tool binding',
        'LangSmith tracing for end-to-end agent observability'
      ],
      color: 'from-cyan-500 to-blue-500',
      bgColor: 'bg-cyan-50',
      borderColor: 'border-cyan-200',
      iconBg: 'bg-cyan-500',
      isActive: true,
    },
    {
      date: 'Nov 2025 - Present',
      title: 'The Pivot to GenAI',
      subtitle: 'AI Engineer Preparation',
      description: 'Recognized the transformative potential of Agentic AI and LLMs. Deep diving into modern AI architectures, building projects, and preparing to bring climate domain expertise to AI engineering.',
      type: 'pivot',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      highlights: ['LLMs', 'LangGraph', 'Agentic Systems', 'MCP Protocol'],
      color: 'from-rose-500 to-pink-500',
      bgColor: 'bg-rose-50',
      borderColor: 'border-rose-200',
      iconBg: 'bg-rose-500',
    },
    {
      date: '2017 - Oct 2025',
      title: 'Software Engineer',
      subtitle: 'NCAR - Research Applications Laboratory',
      description: 'Led development of mission-critical systems powering national weather and water forecasting infrastructure. Served as technical and operational lead across projects for NOAA, NASA, USGS, USAF, and international agencies. Recognized as the team\'s AWS cloud services expert. Delivered client-facing solutions with budget planning and cost-performance analysis.',
      type: 'experience',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
      ),
      highlights: ['WRF-Hydro', 'RFCPS', 'AWS', 'Docker', 'NOAA', 'USGS'],
      achievements: [
        'DICast Microservices Migration: Migrated the DICast forecasting system from monolithic to microservices architecture using Docker. Built REST APIs with Flask RESTPlus and auto-generated Swagger documentation. Designed deployment on AWS ECS, ECR, CloudWatch, and S3',
        'RFCPS (Runway Friction & Closure Prediction): Implemented Stacking ensemble combining Cubist, Neural Networks, and Gradient Tree Boosting to predict runway safety conditions at MSP and DIA airports. Built real-time output generation with SFTP delivery and a web-display viewer for airport stakeholders',
        'IWAA ML Workflow: Devised an ML-based methodology for feature importance analysis using 42 years of hydrological data. Generated basin-averaged daily values to predict Water Year metrics including low flow, high flow, bias, and correlation. Set up calibration framework on Denali HPC and calibrated 1,500+ basins',
        'CONUS404 Forcing Engine: Engineered pipeline processing 65 TB of 1km-resolution climate data (1979-2022) with sub-hourly ingestion (MRMS 2-minute, HRRR 15-minute, GFS 13km variable-frequency). Refactored to object-oriented architecture using abstract classes and Python inheritance. Introduced pytest unit testing framework. Extended short-range config to 48 hours',
        'WRF-Hydro & National Water Model: Core contributor to NOAA\'s operational system forecasting streamflow across 3+ million stream channels. Converted NWM v3.0 retrospective runs and forcings to Zarr format for Puerto Rico, Hawaii, CONUS, and South-Central Alaska',
        'NASA LIS / US Drought Monitor: Adapted PyWrfHydroCalib for NASA\'s HPC environment (discover-milan), navigating disparate system dependencies and SLURM scheduler configurations. Bridged two incompatible WRF-Hydro configurations to enable streamflow drought simulation for the US Drought Monitor',
        'Idaho Cloud Seeding Assessment: Led budget planning and cost-performance analysis for Idaho Department of Water Resources. Set up AWS EC2 with containerized Docker workflows on HPC6a instances, completing a 20-year simulation in under 2 days with parallel execution across multiple machines',
        'Cloud Training Platform: Architected AWS-based SaaS system enabling 150+ scientists globally to train on WRF-Hydro simultaneously, used for AGU, AMS, and CUAHSI training sessions',
        'Kuwait Renewable Energy: Created ML training datasets for StatCast solar and wind forecasting system. Performed verification for a published paper in the Energies journal',
        'METplus Verification: Bridged research and operations for NOAA, USAF, and international meteorological agencies through standardized model verification tools'
      ],
      color: 'from-orange-500 to-amber-500',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      iconBg: 'bg-orange-500',
    },
    {
      date: '2016 - 2017',
      title: 'Student Assistant - DICast®',
      subtitle: 'NCAR - Research Applications Laboratory',
      description: 'Core engineer on NCAR\'s DICast® (Dynamic Integrated foreCast) system, an automated weather forecasting engine that blends multiple weather models with real-time observations to produce optimized point-based forecasts used worldwide.',
      type: 'experience',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      highlights: ['DICast®', 'Machine Learning', 'Forecast Optimization'],
      achievements: [
        'Core engineer developing ML models for DICast\'s forecast optimization engine',
        'DICast powers mission-critical applications worldwide: wildland fire spot forecasts, Federal Highway Administration\'s winter road maintenance system (MDSS), renewable energy forecasting for wind & solar farms, and marine forecasts for commercial shipping & oil rigs',
        'Built statistical blending algorithms combining multiple weather model outputs with observations for superior forecast accuracy'
      ],
      color: 'from-teal-500 to-emerald-500',
      bgColor: 'bg-teal-50',
      borderColor: 'border-teal-200',
      iconBg: 'bg-teal-500',
    },
    {
      date: 'Aug 2014 - Jan 2015',
      title: 'Project Associate',
      subtitle: 'IIT Kanpur',
      description: 'Built an automatic summarization tool for Sanskrit text using NLP techniques including TF-IDF weighting and sentence segmentation.',
      type: 'experience',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      highlights: ['NLP', 'TF-IDF', 'Text Summarization', 'Sanskrit'],
      achievements: [
        'Developed automatic text summarization pipeline for Sanskrit documents using TF-IDF-based sentence scoring and segmentation'
      ],
      color: 'from-emerald-500 to-green-500',
      bgColor: 'bg-emerald-50',
      borderColor: 'border-emerald-200',
      iconBg: 'bg-emerald-500',
    },
    {
      date: '2015 - 2017',
      title: 'Masters in Computer Science',
      subtitle: 'University of Colorado Boulder',
      description: 'GPA: 3.85/4.0. Focused on Data Science, Machine Learning, NLP, and Computer Graphics. Served as Teaching Assistant for Data Structures for 3 semesters.',
      type: 'education',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
        </svg>
      ),
      highlights: ['Data Science', 'Python', 'NLP', 'Machine Learning', 'Graphics'],
      achievements: [
        'Teaching Assistant for Data Structures (3 semesters)',
        'Box Office Prediction using Twitter sentiment analysis (20% accuracy improvement)',
        'Deception Detection with Naive Bayes and SVM classifiers (10% improvement)',
        'Named Entity Recognition using HMM/Viterbi for biomedical text',
        'IBM Great Mind Challenge: placed in top 100 out of 6,000 teams'
      ],
      color: 'from-purple-500 to-pink-500',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      iconBg: 'bg-purple-500',
    },
    {
      date: '2010 - 2014',
      title: 'Bachelor of Technology',
      subtitle: 'Computer Science & Engineering',
      description: 'Built a strong foundation in computer science fundamentals, algorithms, and software development.',
      type: 'education',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
        </svg>
      ),
      highlights: ['Data Structures', 'Algorithms', 'Software Engineering'],
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      iconBg: 'bg-blue-500',
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.dataset.index);
            setVisibleItems((prev) => new Set([...prev, index]));
          }
        });
      },
      { threshold: 0.2 }
    );

    const items = timelineRef.current?.querySelectorAll('.timeline-item');
    items?.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, []);

  return (
    <section id="pivot" className="py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-teal-200/30 to-cyan-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-40 right-10 w-96 h-96 bg-gradient-to-br from-purple-200/30 to-pink-200/30 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-br from-orange-100/20 to-amber-100/20 rounded-full blur-3xl" />
      </div>

      <div className="max-w-5xl mx-auto relative">
        {/* Section Header */}
        <div className="text-center mb-20">
          <span className="inline-block px-4 py-1.5 rounded-full bg-gradient-to-r from-teal-100 to-cyan-100 text-teal-700 text-sm font-medium mb-4">
            My Journey
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            The <span className="bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">Pivot</span> Journey
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            From building climate infrastructure at NCAR to mastering the cutting edge of AI engineering
          </p>
        </div>

        {/* Timeline */}
        <div ref={timelineRef} className="relative">
          {/* Timeline line */}
          <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-cyan-300 via-rose-300 to-blue-300" />

          {/* Timeline events */}
          <div className="space-y-12">
            {timelineEvents.map((event, index) => (
              <div
                key={index}
                data-index={index}
                className={`timeline-item relative flex flex-col md:flex-row items-start gap-6 md:gap-12 transition-all duration-700 ${
                  visibleItems.has(index)
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-8'
                } ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
              >
                {/* Timeline dot */}
                <div className={`absolute left-8 md:left-1/2 -translate-x-1/2 w-10 h-10 rounded-full ${event.iconBg} flex items-center justify-center z-10 shadow-lg`}>
                  <div className="text-white">
                    {event.icon}
                  </div>
                </div>

                {/* Content card */}
                <div
                  className={`ml-20 md:ml-0 md:w-5/12 ${
                    index % 2 === 0 ? 'md:mr-auto md:pr-8' : 'md:ml-auto md:pl-8'
                  }`}
                >
                  <div
                    className={`${event.bgColor} ${event.borderColor} border rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer ${event.isActive ? 'ring-2 ring-cyan-400 ring-offset-2' : ''}`}
                    onClick={() => setExpandedItem(expandedItem === index ? null : index)}
                  >
                    {/* Date badge */}
                    <div className={`inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r ${event.color} text-white text-xs font-semibold mb-3`}>
                      {event.date}
                      {event.isActive && (
                        <span className="ml-2 flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-white opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
                        </span>
                      )}
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-1">
                      {event.title}
                    </h3>
                    {event.subtitle && (
                      <p className={`text-sm font-medium bg-gradient-to-r ${event.color} bg-clip-text text-transparent mb-3`}>
                        {event.subtitle}
                      </p>
                    )}

                    <p className="text-gray-600 text-sm mb-3">
                      {event.description}
                    </p>

                    {/* Collaborator */}
                    {event.collaborator && (
                      <p className="text-sm mb-4 flex items-center gap-2">
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <span className="text-gray-500 italic">{event.collaborator}</span>
                      </p>
                    )}

                    {/* Highlights */}
                    <div className="flex flex-wrap gap-2 mb-3">
                      {event.highlights.map((highlight) => (
                        <span
                          key={highlight}
                          className={`px-2.5 py-1 rounded-full text-xs font-medium ${event.bgColor} ${event.borderColor} border text-gray-700`}
                        >
                          {highlight}
                        </span>
                      ))}
                    </div>

                    {/* Expandable content */}
                    {(event.publications || event.achievements || event.features || event.roadmap) && (
                      <div className={`overflow-hidden transition-all duration-300 ${expandedItem === index ? 'max-h-[800px] overflow-y-auto opacity-100 mt-4' : 'max-h-0 opacity-0'}`}>
                        {event.publications && (
                          <div className="mb-3">
                            <h4 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              Publications
                            </h4>
                            <ul className="space-y-1">
                              {event.publications.map((pub, i) => (
                                <li key={i} className="text-xs text-gray-600 pl-4 border-l-2 border-gray-200">
                                  {pub}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {event.achievements && (
                          <div className="mb-3">
                            <h4 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                              </svg>
                              Key Achievements
                            </h4>
                            <ul className="space-y-1">
                              {event.achievements.map((achievement, i) => (
                                <li key={i} className="text-xs text-gray-600 flex items-start gap-2">
                                  <span className="text-teal-500 mt-0.5">&#10003;</span>
                                  {achievement}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {event.features && (
                          <div className="mb-3">
                            <h4 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                              </svg>
                              Current Features
                            </h4>
                            <ul className="space-y-1">
                              {event.features.map((feature, i) => (
                                <li key={i} className="text-xs text-gray-600 flex items-start gap-2">
                                  <span className="text-cyan-500 mt-0.5">&#9679;</span>
                                  {feature}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {event.roadmap && (
                          <div>
                            <h4 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                              </svg>
                              Roadmap
                            </h4>
                            <ul className="space-y-1">
                              {event.roadmap.map((item, i) => (
                                <li key={i} className="text-xs text-gray-600 flex items-start gap-2">
                                  <span className="text-amber-500 mt-0.5">&#9702;</span>
                                  {item}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Click to expand hint */}
                    {(event.publications || event.achievements || event.features || event.roadmap) && (
                      <div className="flex items-center justify-center mt-2 text-xs text-gray-400">
                        <svg className={`w-4 h-4 transition-transform duration-300 ${expandedItem === index ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                        <span className="ml-1">{expandedItem === index ? 'Less' : 'More details'}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Stats summary */}
        <div className="mt-20 grid grid-cols-3 gap-6">
          {[
            { number: '12', label: 'Publications', color: 'from-teal-500 to-cyan-500' },
            { number: '9', label: 'Citations', color: 'from-purple-500 to-pink-500' },
            { number: '9', label: 'Years at NCAR', color: 'from-orange-500 to-amber-500' },
          ].map((stat, index) => (
            <div key={index} className="text-center p-4 rounded-2xl bg-white/50 backdrop-blur-sm border border-gray-100 shadow-sm">
              <div className={`text-3xl font-bold bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`}>
                {stat.number}
              </div>
              <div className="text-sm text-gray-600 mt-1">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PivotTimeline;
